import { ficheDeLaBulle, type FicheDeBulle, type HabitDeProjet } from '@nextilt/commun';
import { Fiche, useFicheAuPouce } from './Fiche';
import { ficheAuTelephone } from './ficheDuTelephone';
import { laMere } from './ficheEnMots';
import type { FicheTrouvee } from './ficheParSonId';
import { souvenirDe, type useNavigationDeLaFeuille, type useGestesDeFeuille } from '../projets';
import type { useAllerALaFiche } from './useAllerALaFiche';
import { useChoisirLaFiche } from '../partage/useArbre';

/**
 * La fiche ouverte sur une feuille, branchée (sortie de `Feuille.tsx`, chantier
 * nettoyage-feuille, ticket 02) : ses gestes, et où mène chacun de ses liens.
 */
export function FicheDeLaFeuille({
  fiche,
  centreId,
  couleur,
  fichesDuProjet,
  habitDuProjet,
  gestes,
  navigation,
  allerALaFiche,
  onEntrer,
  onRemonterApresSuppression,
  enPage = false,
  onFermerLaPage,
}: {
  fiche: FicheTrouvee;
  /** La bulle de la feuille : supprimée, sa feuille n'existe plus. */
  centreId: string;
  couleur: string | null;
  /** Le type et le papier par défaut des fiches du projet (`Feuille.fiches`, défauts compris). */
  fichesDuProjet: FicheDeBulle;
  /** L'habit du projet (`Feuille.habit`, défauts compris) : l'espace « Habit » de la fiche en part. */
  habitDuProjet: HabitDeProjet;
  gestes: ReturnType<typeof useGestesDeFeuille>;
  navigation: ReturnType<typeof useNavigationDeLaFeuille>;
  allerALaFiche: ReturnType<typeof useAllerALaFiche>;
  /** Entrer dans une bulle : sa feuille. */
  onEntrer: (id: string) => void;
  /** Le centre supprimé : on remonte d'un cran. */
  onRemonterApresSuppression: () => void;
  /** Au téléphone, dans un projet, la fiche du centre en page (chantier telephone-projet, 04). */
  enPage?: boolean;
  /** En page, ce qui la ferme : on remonte d'un cran. */
  onFermerLaPage?: () => void;
}) {
  const { bulle, chemin } = fiche;
  // Ce que la fiche saura défaire d'un clic (ticket 29), lu à chaque rendu.
  const souvenir = souvenirDe(bulle);

  /*
   * Le type et le papier qu'elle montre (chantier types-de-fiche, modification 02) : la règle
   * écrite une fois, `ficheDeLaBulle` (celui de la bulle, sinon celui du projet, sinon
   * Classique), lue avec ce que la feuille porte du projet. Au téléphone, une bulle à qui
   * personne n'a donné de type s'ouvre en Note (`ficheAuTelephone`, chantier telephone-fiche, 01).
   */
  const choisirLaFiche = useChoisirLaFiche();
  const auPouce = useFicheAuPouce();
  const enBase = { typeDeFiche: bulle.typeDeFiche ?? null, papierDeFiche: bulle.papierDeFiche ?? null };
  const montree = auPouce
    ? ficheAuTelephone(enBase, fichesDuProjet)
    : ficheDeLaBulle(enBase, { typeParDefaut: fichesDuProjet.type, papierParDefaut: fichesDuProjet.papier });

  /*
   * Une étape ouverte depuis « Ses étapes » (ticket 08 du chantier telephone) : elle vit sur la
   * feuille de la bulle dont la fiche est ouverte. Sous la feuille (le centre, une bulle de la
   * feuille, une étape déjà lointaine), l'étape s'ouvre sur place, comme une bulle dépliée de la
   * Hiérarchie. Sinon (une sœur du centre, par un intercalaire), on entre dans la bulle et sa
   * fiche s'y ouvre : le fil reste juste.
   */
  const ouvrirUneEtape = (i: string) => void allerALaFiche(i, { arrivee: 'chez-sa-mere' });
  /*
   * « ‹ la mère », en haut de la fiche (chantier `revue-design`, ticket 02) : la fiche de la
   * bulle qui contient celle-ci. Sous le centre, elle s'ouvre sur place, comme une bulle
   * dépliée de la Hiérarchie (le centre lui-même, une bulle de la feuille, une étape dépliée).
   * Au-dessus du centre, ou hors de son chemin (une sœur du centre), on remonte dans la mère :
   * sa feuille s'ouvre, sa fiche par-dessus, et le fil reste juste.
   */
  const ouvrirLaMere = () => {
    const mere = laMere(chemin);
    if (!mere) return;
    void allerALaFiche(mere.id, { arrivee: 'chez-elle', chemin: chemin.slice(0, -1) });
  };
  /*
   * Une bulle qu'elle attend, touchée dans la pastille « Attend » de la fiche du téléphone
   * (chantier `telephone-projets-fiche`, ticket 03, décision 16 de la revue) : sa fiche s'ouvre.
   * Sur la feuille ou plus bas, sur place, comme une ligne d'« Ici, tu peux faire ». Ailleurs
   * (un autre projet, plus haut), on va sur la feuille de sa mère et sa fiche s'y ouvre, comme
   * depuis l'Agenda : on la voit parmi ses sœurs.
   */
  const fermer = onFermerLaPage ?? navigation.fermer;
  const ouvrirUneAutreBulle = (i: string) =>
    void allerALaFiche(i, { arrivee: 'chez-sa-mere', avantDAller: navigation.fermer });

  return (
    <Fiche
      bulle={bulle}
      chemin={chemin}
      couleur={couleur}
      habitDuProjet={{ couleur, forme: habitDuProjet.forme, trait: habitDuProjet.trait }}
      typeEtPapier={{
        montree,
        enBase,
        duProjet: fichesDuProjet,
        pasEcrit: choisirLaFiche.isError,
        onChoisir: (changement) => choisirLaFiche.mutate({ id: bulle.id, changement }),
      }}
      onChanger={(champs) => gestes.modifierBulle(bulle.id, champs)}
      onSoeur={navigation.ouvrir}
      onMere={ouvrirLaMere}
      onEtape={ouvrirUneEtape}
      onValider={() => {
        gestes.validerBulle(bulle.id);
        // « C'est fait » ferme la fiche partout, téléphone compris (chantier `la-fiche`,
        // décision d'Igor 2, faite au chantier `revue-design`, ticket 02) : pour annuler,
        // on rouvre la fiche. En page, on remonte d'un cran.
        fermer();
      }}
      onAttendre={(champs) => gestes.attendre(bulle, champs)}
      onAutreBulle={ouvrirUneAutreBulle}
      onDefaire={() => gestes.defaire(bulle)}
      annulation={{
        etatAvant: souvenir?.etatAvant ?? null,
        avecInventaire: souvenir?.avecInventaire ?? false,
        enCours: gestes.defaireEnCours,
      }}
      onCreuser={() => {
        navigation.fermer();
        onEntrer(bulle.id);
      }}
      onFermer={fermer}
      enPage={enPage}
      onSupprimee={() => {
        navigation.fermer();
        // Le centre supprimé : sa feuille n'existe plus, on remonte d'un cran.
        if (bulle.id === centreId) onRemonterApresSuppression();
      }}
      /*
       * Rangée ailleurs (chantier `deplacer`) : la fiche se ferme et les feuilles se relisent
       * (`useDeplacer`). Le centre déplacé existe toujours : on reste sur sa feuille, qui se
       * relit avec son nouveau fil et sa nouvelle couleur.
       */
      onDeplacee={navigation.fermer}
    />
  );
}
